import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

function DeveloperSubmit() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleScreenshots = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      setError(`Some images exceed 5MB limit: ${oversized.map((f) => f.name).join(", ")}`);
      e.target.value = "";
      return;
    }
    const remaining = 10 - screenshots.length;
    setScreenshots((prev) => [...prev, ...files.slice(0, remaining)].slice(0, 10));
    e.target.value = "";
  };

  const removeScreenshot = (index) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    setUploadProgress(0);

    const form = e.target;
    const fd = new FormData();
    fd.append("title", form.title.value);
    fd.append("description", form.description.value);
    fd.append("category_id", form.category_id.value);
    fd.append("price", form.price.value);
    fd.append("version", form.version.value);
    if (form.changelog.value) fd.append("changelog", form.changelog.value);

    if (form.cover.files[0]) {
      if (form.cover.files[0].size > MAX_FILE_SIZE) {
        setError("Cover image exceeds 10MB limit");
        setSubmitting(false);
        return;
      }
      fd.append("cover", form.cover.files[0]);
    }
    if (form.thumbnail.files[0]) {
      if (form.thumbnail.files[0].size > MAX_FILE_SIZE) {
        setError("Thumbnail exceeds 10MB limit");
        setSubmitting(false);
        return;
      }
      fd.append("thumbnail", form.thumbnail.files[0]);
    }
    if (form.trailer_url.value) fd.append("trailer_url", form.trailer_url.value);
    if (form.download_link.value) fd.append("download_link", form.download_link.value);

    screenshots.forEach((file) => fd.append("screenshots[]", file));

    try {
      await api.upload("/developer/games", fd, setUploadProgress);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (submitted) {
    return (
      <div className="dev-submit-success">
        <h2>Game Submitted!</h2>
        <p>Your game has been submitted for review. We'll get back to you soon.</p>
        <Link to="/" className="browse-link">Back to Browse</Link>
      </div>
    );
  }

  return (
    <div className="dev-submit">
      <h2>Submit Your Game</h2>
      <p className="dev-submit-intro">
        Don't host APKs yourself. Just provide the details and external download links.
      </p>
      {error && <p className="auth-error">{error}</p>}
      <form ref={formRef} onSubmit={handleSubmit} className="dev-submit-form">
        <label>
          Game Title
          <input type="text" name="title" required placeholder="e.g. My Awesome Game" />
        </label>
        <label>
          Description
          <textarea name="description" required rows="4" placeholder="Describe your game..." />
        </label>
        <div className="form-row">
          <label>
            Category
            <select name="category_id" required>
              <option value="">Select category</option>
              <option value="1">Action RPG</option>
              <option value="2">Platformer</option>
              <option value="3">Racing</option>
              <option value="4">Puzzle</option>
              <option value="5">Survival</option>
              <option value="6">Simulation</option>
              <option value="7">Strategy</option>
              <option value="8">Adventure</option>
            </select>
          </label>
          <label>
            Price (Tshs)
            <input type="number" name="price" required min="0" placeholder="e.g. 50000" />
          </label>
        </div>
        <label>
          Cover Image
          <input type="file" name="cover" accept="image/*" required />
        </label>
        <label>
          Thumbnail
          <input type="file" name="thumbnail" accept="image/*" required />
        </label>
        <label>
          Trailer URL (YouTube link)
          <input type="url" name="trailer_url" placeholder="https://youtube.com/watch?v=..." />
        </label>
        <label className="screenshots-label">
          <span>Screenshots (max 10 images)</span>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleScreenshots}
            disabled={screenshots.length >= 10}
          />
          {screenshots.length > 0 && (
            <div className="screenshot-previews">
              {screenshots.map((file, i) => (
                <div key={i} className="screenshot-preview-item">
                  <img src={URL.createObjectURL(file)} alt={`Screenshot ${i + 1}`} />
                  <button type="button" className="remove-screenshot-btn" onClick={() => removeScreenshot(i)}>
                    &times;
                  </button>
                  <span className="upload-filename">{file.name}</span>
                </div>
              ))}
            </div>
          )}
          {screenshots.length > 0 && (
            <p className="screenshot-count">{screenshots.length} / 10 images selected</p>
          )}
        </label>
        <fieldset className="versions-fieldset">
          <legend>Initial Version</legend>
          <div className="form-row">
            <label>
              Version
              <input type="text" name="version" required placeholder="1.0.0" />
            </label>
            <label>
              Download Link (Google Drive, Dropbox, GitHub, etc.)
              <input type="url" name="download_link" required placeholder="https://..." />
            </label>
          </div>
          <label>
            Changelog
            <textarea name="changelog" rows="2" placeholder="What's new in this version?" />
          </label>
        </fieldset>
        <button type="submit" className="submit-game-btn" disabled={submitting}>
          {submitting ? "Uploading..." : "Submit Game"}
        </button>
        {submitting && (
          <div className="upload-progress">
            <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
            <span className="upload-progress-text">{uploadProgress}%</span>
          </div>
        )}
      </form>
    </div>
  );
}

export default DeveloperSubmit;
